import React, { useEffect, useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ActionListChildren,
  Avatar,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  Tooltip,
} from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { getCustomerWalletActions } from 'pages/Customers/helpers/utils'
import { BulkCapabilityResponse } from 'types/capabilities/capabilities'
import {
  CustomerWallet,
  CustomerWalletStatusType,
  LedgerWalletCapabilities,
  OrchestratedCustomerWallet,
} from 'types/customers'
import { numberWithCommas } from 'utils/utils'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'

interface UseCustomerWalletsTableRowDataProps {
  customerWallets: CustomerWallet[]
  isActiveCustomer: boolean
  bulkCapabilities: BulkCapabilityResponse
  isFetchingCapabilities: boolean
  handleExportTransactionClick: (customerWallet: CustomerWallet) => void
  handleOnDetailsClick: (index: number) => void
  handleOpenMoveSheet: (wallet: CustomerWallet) => void
  handleOpenFiatDepositSheet: (customerWallet: CustomerWallet) => void
  handleOpenSendFiatSheet: (customerWallet: CustomerWallet) => void
  handleOpenSendCryptoSheet: (customerWallet: CustomerWallet) => void
}

export const useCustomerWalletsTableRowData = ({
  bulkCapabilities,
  isFetchingCapabilities,
  customerWallets,
  isActiveCustomer,
  handleExportTransactionClick,
  handleOnDetailsClick,
  handleOpenMoveSheet,
  handleOpenFiatDepositSheet,
  handleOpenSendFiatSheet,
  handleOpenSendCryptoSheet,
}: UseCustomerWalletsTableRowDataProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { viewOnly: isViewOnlyRole } = useProfileContext() || {}
  const { accountCapabilities } = useAccountsContext()

  const customerModel = useAtomValue(customerModelAtom)

  const [tableRows, setTableRows] = useState<ActionListChildren[]>([])

  const handleOnTableRowClick = (customerWallet: CustomerWallet) => {
    navigate(`${PathNames.CUSTOMERS}/${customerWallet?.customerReference}/${customerWallet?.id}`)
  }

  const getActiveCustomerWalletMenuItems = (
    customerWallet: OrchestratedCustomerWallet,
    customerWalletListIndex: number
  ) => {
    const {
      // crypto and fiat
      isCryptoSendOptionEnabled,
      isTransactionExportOptionEnabled,
      // fiat wallet actions
      isUnifiedPayoutOptionEnabled,
      isFiatMoveOptionEnabled,
      isFiatDepositOptionEnabled,
      // crypto wallet actions
      isCryptoReceiveOptionEnabled,
      isCryptoMoveOptionEnabled,
    } = getCustomerWalletActions({
      accountCapabilities,
      customerWallet,
      isViewOnlyRole,
      customerModel,
    })

    const { id } = customerWallet || {}

    return [
      <DropdownMenuItem
        key={`details-option-${customerWallet?.id}`}
        LeadingIcon={<Icon name="Info" />}
        onClick={() => handleOnDetailsClick(customerWalletListIndex)}
      >
        {t('details')}
      </DropdownMenuItem>,
      ...(isTransactionExportOptionEnabled
        ? [
            <DropdownMenuItem
              key={`transaction-export-option-${id}`}
              LeadingIcon={<Icon name="DownloadSimple" />}
              onClick={() => handleExportTransactionClick(customerWallet)}
            >
              {t('export')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isCryptoSendOptionEnabled
        ? [
            <DropdownMenuItem
              key={`crypto-send-option-${id}`}
              LeadingIcon={<Icon name="ArrowUpRight" />}
              onClick={() => handleOpenSendCryptoSheet(customerWallet)}
            >
              {t('payments.sendCrypto')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isCryptoReceiveOptionEnabled
        ? [
            <DropdownMenuItem
              key={`crypto-receive-option-${id}`}
              LeadingIcon={<Icon name="ArrowDownLeft" />}
              onClick={() => {
                navigate(
                  `${PathNames.CUSTOMERS}/${customerWallet?.customerReference}/${customerWallet?.id}?tab=receive-crypto`
                )
              }}
            >
              {t('payments.receiveCrypto')}
            </DropdownMenuItem>,
          ]
        : []),

      ...(isUnifiedPayoutOptionEnabled
        ? [
            <DropdownMenuItem
              key={`fiat-send-option-${id}`}
              LeadingIcon={<Icon name="CoinArrowOutCustom" />}
              onClick={() => handleOpenSendFiatSheet(customerWallet)}
            >
              {t('wallets.fiatSend.sendFiat')}
            </DropdownMenuItem>,
          ]
        : []),

      ...(isFiatMoveOptionEnabled || isCryptoMoveOptionEnabled
        ? [
            <DropdownMenuItem
              key={`move-option-${id}`}
              LeadingIcon={<Icon name="ArrowsLeftRight" />}
              onClick={() => handleOpenMoveSheet(customerWallet)}
            >
              {t('customers.move')}
            </DropdownMenuItem>,
          ]
        : []),
      ...(isFiatDepositOptionEnabled
        ? [
            <DropdownMenuItem
              key={`deposit-option-${id}`}
              LeadingIcon={<Icon name="CoinArrowInCustom" />}
              onClick={() => handleOpenFiatDepositSheet(customerWallet)}
            >
              {t('transactionAction.deposit')}
            </DropdownMenuItem>,
          ]
        : []),
    ]
  }

  const getCustomerWalletMenuItems = (
    customerWallet: OrchestratedCustomerWallet,
    customerWalletIndex: number
  ) => {
    const isWalletActive = customerWallet?.status === CustomerWalletStatusType.ACTIVE

    if (!isWalletActive) {
      const { isTransactionExportOptionEnabled } = getCustomerWalletActions({
        accountCapabilities,
        customerWallet,
        isViewOnlyRole,
        customerModel,
      })
      return [
        <DropdownMenuItem
          key={`details-option-inactive-${customerWallet?.id}`}
          LeadingIcon={<Icon name="Info" />}
          onClick={() => handleOnDetailsClick(customerWalletIndex)}
        >
          {t('details')}
        </DropdownMenuItem>,
        ...(isTransactionExportOptionEnabled
          ? [
              <DropdownMenuItem
                key={`transaction-export-option-inactive-${customerWallet?.id}`}
                LeadingIcon={<Icon name="DownloadSimple" />}
                onClick={() => handleExportTransactionClick(customerWallet)}
              >
                {t('export')}
              </DropdownMenuItem>,
            ]
          : []),
      ]
    }

    return [
      ...(isActiveCustomer
        ? getActiveCustomerWalletMenuItems(customerWallet, customerWalletIndex)
        : []),
    ]
  }

  const wallets: OrchestratedCustomerWallet[] = useMemo(() => {
    return customerWallets?.map(customerWallet => {
      const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
        getFiatWalletActionSupport({
          walletProperties: {
            currency: customerWallet?.balance?.currencyCode,
            lsid: customerWallet?.id,
          },
          bulkCapabilities,
        })

      return {
        ...customerWallet,
        supportsDeposits: supportsDeposits,
        supportsWithdrawals: supportsWithdrawals,
        supportsThirdParty: supportsPay,
        supportsInternalTransfers: supportsInternalTransfers,
      }
    })
  }, [bulkCapabilities, customerWallets])

  useEffect(() => {
    if (!isFetchingCapabilities) {
      const rows: ActionListChildren[] = wallets?.map((customerWallet, index) => {
        const { id, balance, status, name, capabilities } = customerWallet || {}
        const { currencyCode, value } = balance || {}

        const isWalletActive = status === CustomerWalletStatusType.ACTIVE
        const customerWalletMenuItems = getCustomerWalletMenuItems(customerWallet, index)
        const isSafeguarded = capabilities?.includes(LedgerWalletCapabilities.SAFEGUARDED)

        return {
          transparent: true,
          key: id,
          id: `wallet-row-${id}`,
          title: name,
          testid: `wallet-row-${id}`,
          ...(isSafeguarded && {
            HeaderHighlightLeft: (
              <Tooltip
                position="right"
                bodyContent={t('wallets.safeguardTooltip')}
                type="complex"
                actionButton={
                  <Button
                    variant="ghost"
                    noPadding
                    onClick={() =>
                      window.open(
                        process.env.SAFEGUARD_LEARN_MORE_LINK,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    {t('wallets.safeguardTooltipSeeHere')}
                  </Button>
                }
              >
                <div
                  onMouseEnter={e => e.stopPropagation()}
                  onMouseLeave={e => e.stopPropagation()}
                >
                  <Highlight
                    type="success"
                    variant="emphasis"
                    iconElement={<Icon size="small" name="ShieldCheck" />}
                  >
                    {t('wallets.safeguarded')}
                  </Highlight>
                </div>
              </Tooltip>
            ),
          }),
          ...(!isWalletActive && {
            ActionHighlight: (
              <Box className="pr-2">
                <Highlight
                  type="warning"
                  variant="emphasis"
                  iconElement={<Icon name="WarningCircle" />}
                >
                  {customerWallet?.status}
                </Highlight>
              </Box>
            ),
          }),

          Avatar: (
            <Box paddingL={8}>
              <Box className="relative">
                <Avatar name={currencyCode} />
              </Box>
            </Box>
          ),

          BodyPre: currencyCode?.toLocaleUpperCase(),
          onItemClick: () => {
            handleOnTableRowClick(customerWallet)
          },
          RightPrimaryText: `${numberWithCommas(value)} ${currencyCode}`,
          // To prevent showing empty dropdown menu
          CustomAction: customerWalletMenuItems?.length ? (
            <DropdownMenu DropdownMenuItems={customerWalletMenuItems}>
              <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
            </DropdownMenu>
          ) : (
            <Button
              disabled={true}
              iconElement={<Icon name="DotsThreeVerticalCustom" />}
              variant="ghost"
            />
          ),
        }
      })

      setTableRows(rows)
    }
  }, [wallets, isFetchingCapabilities])

  return tableRows
}
